import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { auditApi } from '../api/client'
import './RunDetailDrawer.css'

interface Props { runId: string | null; onClose: () => void }

export function RunDetailDrawer({ runId, onClose }: Props) {
  useEffect(() => {
    if (!runId) return
    // Capture phase + preventDefault: this drawer stacks above ModuleDrawer,
    // whose bubble-phase window listener ignores a defaultPrevented Escape.
    // Capture guarantees we run first even though we register later.
    const h = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      onClose()
    }
    window.addEventListener('keydown', h, true)
    return () => window.removeEventListener('keydown', h, true)
  }, [runId, onClose])
  const q = useQuery({
    queryKey: ['run-detail', runId],
    queryFn: () => auditApi.runDetail(runId as string),
    enabled: !!runId,
  })
  if (!runId) return null
  return (
    <div className="run-drawer-backdrop" onClick={onClose}>
      <aside className="run-drawer" onClick={(e) => e.stopPropagation()}
             role="dialog" aria-modal="true" aria-labelledby="run-drawer-title">
        <header className="run-drawer-head">
          <h3 id="run-drawer-title">Run {runId.slice(0, 8)}</h3>
          <button className="run-drawer-x" onClick={onClose}
                  aria-label="Close" autoFocus>×</button>
        </header>
        {q.isLoading && <p className="run-drawer-muted">Loading…</p>}
        {q.isError && <p className="run-drawer-muted">Failed to load run detail.</p>}
        {q.data && (
          <div className="run-drawer-body">
            <section>
              <h4>Run</h4>
              <p>started {q.data.run.started_at ?? '—'} ·{' '}
                {q.data.run.success ? 'success' : 'failed'}</p>
              <p className="run-drawer-counts">
                signals {q.data.counts.signals_generated}/{q.data.counts.signals_passed_sanity}
                {' · '}orders {q.data.counts.orders_placed} · fills {q.data.counts.fills_received}
              </p>
              <p className="run-drawer-note">{q.data.note}</p>
            </section>
            <section>
              <h4>Agent decisions ({q.data.agent_decisions.length})</h4>
              <ul>
                {q.data.agent_decisions.map((a, i) => (
                  <li key={i}>
                    {a.agent_name} · {a.symbol} · {a.signal} · {a.confidence.toFixed(2)}
                  </li>
                ))}
              </ul>
            </section>
            <section>
              <h4>Sanity-gate events ({q.data.sanity_gate_events.length})</h4>
              <ul>
                {q.data.sanity_gate_events.map((s, i) => (
                  <li key={i} data-decision={s.decision}>
                    {s.rule_name} · {s.symbol} · <b>{s.decision}</b> · {s.reason_detail}
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </aside>
    </div>
  )
}
